import { RubyGemMark } from '../../components/RubyGemMark';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, Link2, Plus, RefreshCw, Save, Search, Trash2, X,
} from 'lucide-react';
import { FieldLabel, IconAlert } from '@documents-ui';
import {
  ACCEPTANCE_MODES, BINDING_SUBJECT_TYPES, DEFAULT_ACCEPTANCE_MODE_BY_KIND, SIGNER_ROLES,
} from '@platform/shared-types';
import type {
  AcceptanceMode, BindingItem, BindingSubject, BindingSubjectType, DocumentBinding,
  LegalDocument, ResolvedBindingItem, SignerRole,
} from '@platform/shared-types';
import { StaffPage } from '../../components/staff/StaffChrome';
import LegalNav from './LegalNav';
import SearchableDropdown from '../../components/SearchableDropdown';
import ConfirmModal from '../../components/credits/ConfirmModal';
import { documentsApi } from '../../services/documentsApi';
import { documentsExtApi } from '../../services/documentsExtApi';
import { toastService } from '../../services/toastService';
import { categoryLabel, loadCategories } from './documentCategories';
import { Button, INPUT_CLASS } from './rubyChrome';
import { RubyLoader } from '../../components/RubyLoader';

/**
 * LEGAL / BINDINGS — the one place that says which documents a device, a
 * financing term, a package, a checkout, a business type or a jurisdiction
 * owes (spec 2026-08-29 §6, §8). A binding row is CONFIG: `PUT` replaces it
 * wholesale, `DELETE` genuinely removes it — there is no retire-not-delete
 * rule here the way there is for a document or a pack, because nothing
 * downstream freezes a snapshot of a binding the way a request freezes a
 * pack's item list.
 *
 * The point of the screen is the Preview panel at the bottom: pick the
 * subjects a real scenario would name, call the SAME pure resolver the
 * binder (P3) will call, and see the resolved, deduped, ordered list with
 * `boundBy` chips explaining why each document is present.
 */

/* ── guarded runtime imports ───────────────────────────────────────────────
 *
 * `BINDING_SUBJECT_TYPES`, `ACCEPTANCE_MODES`, `DEFAULT_ACCEPTANCE_MODE_BY_KIND`
 * and `SIGNER_ROLES` are RUNTIME values from `@platform/shared-types`. A Vite
 * dep pre-bundle built before one of these exports existed hands back
 * `undefined` for it — at MODULE SCOPE, which would white-screen this whole
 * page (a `.map()` over `undefined`) instead of failing where it is used.
 * This repo already learned that lesson in `documentCategories.ts` and
 * `CompanyProfilePage.tsx`; every value below degrades instead of crashing.
 * The remedy is always the same: RESTART the dev server — deleting
 * `node_modules/.vite` under a running server does nothing, because Vite
 * keeps the optimised bundle in memory and rewrites the directory
 * underneath it.
 */
function warnStaleSharedTypesExport(what: string, degradedTo: string): void {
  console.error(
    `@platform/shared-types exported no usable ${what} on BindingsPage — a stale Vite `
    + 'pre-bundle. RESTART the dev server: deleting node_modules/.vite under a running '
    + `server does nothing, because Vite keeps the optimised bundle in memory and rewrites `
    + `the directory. ${degradedTo}`,
  );
}

/** Compiled-in fallback — identical to the source list in `documentBindings.ts`. */
const FALLBACK_SUBJECT_TYPES: readonly BindingSubjectType[] = [
  'device_model', 'device_sku', 'financing_term', 'financing_policy',
  'software_package', 'subscription_plan', 'checkout', 'business_type', 'jurisdiction',
];

const SUBJECT_TYPES: readonly BindingSubjectType[] = (() => {
  if (Array.isArray(BINDING_SUBJECT_TYPES) && BINDING_SUBJECT_TYPES.length) {
    return BINDING_SUBJECT_TYPES as readonly BindingSubjectType[];
  }
  warnStaleSharedTypesExport(
    'BINDING_SUBJECT_TYPES (not an array)',
    'Falling back to the compiled-in list so subject-type pickers keep all nine options.',
  );
  return FALLBACK_SUBJECT_TYPES;
})();

/** Compiled-in fallback — identical to `documentAcceptanceModes.ts`. */
const FALLBACK_ACCEPTANCE_MODES: readonly AcceptanceMode[] = [
  'acknowledge_once', 'acknowledge_each_version', 'sign_once', 'sign_each_version',
];

const ACCEPTANCE_MODE_LIST: readonly AcceptanceMode[] = (() => {
  if (Array.isArray(ACCEPTANCE_MODES) && ACCEPTANCE_MODES.length) {
    return ACCEPTANCE_MODES as readonly AcceptanceMode[];
  }
  warnStaleSharedTypesExport(
    'ACCEPTANCE_MODES (not an array)',
    'Falling back to the compiled-in list so the acceptance-mode picker keeps all four options.',
  );
  return FALLBACK_ACCEPTANCE_MODES;
})();

/** Compiled-in fallback — identical to `documentAcceptanceModes.ts`. */
const FALLBACK_DEFAULT_MODE_BY_KIND: Readonly<Record<'policy' | 'agreement', AcceptanceMode>> = {
  policy: 'acknowledge_each_version',
  agreement: 'sign_once',
};

const DEFAULT_MODE_BY_KIND: Readonly<Record<'policy' | 'agreement', AcceptanceMode>> = (() => {
  const mod: any = DEFAULT_ACCEPTANCE_MODE_BY_KIND;
  if (mod && typeof mod === 'object' && mod.policy && mod.agreement) {
    return mod as Readonly<Record<'policy' | 'agreement', AcceptanceMode>>;
  }
  warnStaleSharedTypesExport(
    'DEFAULT_ACCEPTANCE_MODE_BY_KIND (missing policy/agreement)',
    'Falling back to the compiled-in defaults (policy → acknowledge each version, agreement → sign once).',
  );
  return FALLBACK_DEFAULT_MODE_BY_KIND;
})();

/** Compiled-in fallback — identical to `documentSigning.ts`. */
const FALLBACK_SIGNER_ROLES: readonly SignerRole[] = [
  'platform', 'tenant_admin', 'employee', 'customer', 'witness', 'guarantor', 'partner',
];

const SIGNER_ROLE_LIST: readonly SignerRole[] = (() => {
  if (Array.isArray(SIGNER_ROLES) && SIGNER_ROLES.length) {
    return SIGNER_ROLES as readonly SignerRole[];
  }
  warnStaleSharedTypesExport(
    'SIGNER_ROLES (not an array)',
    'Falling back to the compiled-in list so signer-role chips keep all seven roles.',
  );
  return FALLBACK_SIGNER_ROLES;
})();

/* ── labels ─────────────────────────────────────────────────────────────── */

const SUBJECT_TYPE_LABEL: Record<BindingSubjectType, string> = {
  device_model: 'Device model',
  device_sku: 'Device SKU',
  financing_term: 'Financing term',
  financing_policy: 'Financing policy',
  software_package: 'Software package',
  subscription_plan: 'Subscription plan',
  checkout: 'Checkout',
  business_type: 'Business type',
  jurisdiction: 'Jurisdiction',
};
const subjectTypeLabel = (t: string): string => SUBJECT_TYPE_LABEL[t as BindingSubjectType] ?? t;

const ACCEPTANCE_MODE_LABEL: Record<AcceptanceMode, string> = {
  acknowledge_once: 'Acknowledge once',
  acknowledge_each_version: 'Acknowledge each version',
  sign_once: 'Sign once',
  sign_each_version: 'Sign each version',
};

const ROLE_LABEL: Record<string, string> = {
  platform: 'Platform (countersign)',
  tenant_admin: 'Tenant admin',
  employee: 'Employee',
  customer: 'Customer',
  witness: 'Witness',
  guarantor: 'Guarantor',
  partner: 'Partner',
};

/** Roles the requests engine can actually route to a signing surface today — same restriction as `PacksPage`. */
const OFFERED_ROLES: SignerRole[] = (['tenant_admin', 'guarantor', 'partner', 'platform'] as SignerRole[])
  .filter((r) => (SIGNER_ROLE_LIST as readonly string[]).includes(r));

const SUBJECT_TYPE_OPTIONS = SUBJECT_TYPES.map((t) => ({ value: t, label: SUBJECT_TYPE_LABEL[t] }));
const ACCEPTANCE_MODE_OPTIONS = ACCEPTANCE_MODE_LIST.map((m) => ({ value: m, label: ACCEPTANCE_MODE_LABEL[m] ?? m }));

/** Lands in a DynamoDB sort key server-side — mirrors the server's `SubjectId` regex exactly. */
const SUBJECT_ID_RE = /^[A-Za-z0-9_.-]{1,64}$/;

/** Same key shape as the server's `bindingSubjectKey` — a one-line pure function, defined locally rather than as a fifth guarded import. */
const subjectKey = (s: { subjectType: string; subjectId: string }): string => `${s.subjectType}#${s.subjectId}`;

/* ── small local primitives (ruby light-chrome, 44px targets) ─────────────── */

const Toggle: React.FC<{ on: boolean; onClick: () => void; disabled?: boolean; label?: string }> = ({
  on, onClick, disabled, label,
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={label}
    disabled={disabled}
    onClick={onClick}
    className={`relative h-6 w-[42px] shrink-0 rounded-full transition disabled:cursor-not-allowed disabled:opacity-40 ${on ? 'bg-admin-primary' : 'bg-stone-300'}`}
  >
    <span className={`absolute top-[3px] h-[18px] w-[18px] rounded-full bg-white transition-all ${on ? 'right-[3px]' : 'left-[3px]'}`} />
  </button>
);

function LightWarn({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs leading-relaxed text-amber-800">
      <IconAlert size={14} className="mt-0.5 shrink-0" />
      <div>{children}</div>
    </div>
  );
}

/* ── editor state ───────────────────────────────────────────────────────── */

interface EditorState {
  isNew: boolean;
  subjectType: BindingSubjectType | '';
  subjectId: string;
  note: string;
  items: BindingItem[];
}

const newDefaultRoles = (kind: 'policy' | 'agreement'): SignerRole[] => (
  kind === 'agreement' ? OFFERED_ROLES.slice(0, 1) : []
);

export default function BindingsPage() {
  const [bindings, setBindings] = useState<DocumentBinding[] | null>(null);
  const [documents, setDocuments] = useState<LegalDocument[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [loadingEditor, setLoadingEditor] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<BindingSubject | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [previewSubjects, setPreviewSubjects] = useState<BindingSubject[]>([]);
  const [previewType, setPreviewType] = useState<BindingSubjectType | ''>('');
  const [previewId, setPreviewId] = useState('');
  const [previewResult, setPreviewResult] = useState<
    { items: ResolvedBindingItem[]; subjectCount: number; boundSubjectCount: number } | null
  >(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const documentById = useMemo(
    () => new Map(documents.map((d) => [d.documentId, d] as const)),
    [documents],
  );

  const load = useCallback(async () => {
    setLoadingList(true);
    setListError(null);
    try {
      const [b, d] = await Promise.all([
        documentsExtApi.listBindings(),
        documentsApi.listDocuments({ status: 'active' }),
      ]);
      setBindings(b.bindings);
      setDocuments(d.documents || []);
    } catch (e: any) {
      setListError(e?.message || 'bindings_unavailable');
      setBindings(null);
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => { void load(); void loadCategories(); }, [load]);

  /** Bindings, grouped by subject type in the registry's own declared order, subjects alphabetical within a group. */
  const groups = useMemo(() => {
    if (!bindings) return [];
    const bySubject = new Map<BindingSubjectType, DocumentBinding[]>();
    for (const b of bindings) {
      const t = b.subjectType as BindingSubjectType;
      bySubject.set(t, [...(bySubject.get(t) ?? []), b]);
    }
    const order = SUBJECT_TYPES;
    const known = new Set(order);
    const extra = [...bySubject.keys()].filter((t) => !known.has(t)).sort();
    return [...order, ...extra]
      .filter((t) => bySubject.has(t))
      .map((t) => ({
        subjectType: t,
        rows: [...(bySubject.get(t) ?? [])].sort((a, b2) => a.subjectId.localeCompare(b2.subjectId)),
      }));
  }, [bindings]);

  const openCreate = () => {
    setSelectedKey(null);
    setEditor({ isNew: true, subjectType: '', subjectId: '', note: '', items: [] });
  };

  const selectBinding = async (b: DocumentBinding) => {
    const key = subjectKey(b);
    setSelectedKey(key);
    setLoadingEditor(true);
    try {
      // Fresh read rather than the row already in `bindings` — another admin
      // may have changed it since the list loaded.
      const res = await documentsExtApi.getBinding(b.subjectType, b.subjectId);
      const fresh = res?.binding ?? b;
      setEditor({
        isNew: false,
        subjectType: fresh.subjectType as BindingSubjectType,
        subjectId: fresh.subjectId,
        note: fresh.note ?? '',
        items: fresh.items,
      });
    } catch (e: any) {
      toastService.error(e?.message || 'binding_load_failed');
      setEditor(null);
      setSelectedKey(null);
    } finally {
      setLoadingEditor(false);
    }
  };

  const closeEditor = () => { setEditor(null); setSelectedKey(null); };

  /* ── item editing ─────────────────────────────────────────────────────── */

  const patchItem = (idx: number, patch: Partial<BindingItem>) => {
    if (!editor) return;
    setEditor({ ...editor, items: editor.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)) });
  };

  const removeItem = (idx: number) => {
    if (!editor) return;
    setEditor({ ...editor, items: editor.items.filter((_, i) => i !== idx) });
  };

  const toggleRole = (idx: number, role: SignerRole) => {
    if (!editor) return;
    const item = editor.items[idx];
    const has = item.signerRoles.includes(role);
    patchItem(idx, { signerRoles: has ? item.signerRoles.filter((r) => r !== role) : [...item.signerRoles, role] });
  };

  const addItem = (documentId: string) => {
    if (!editor || !documentId) return;
    const doc = documentById.get(documentId);
    if (!doc || editor.items.some((i) => i.documentId === documentId)) return;
    const kind = doc.kind as 'policy' | 'agreement';
    setEditor({
      ...editor,
      items: [...editor.items, {
        documentId,
        kind,
        required: true,
        signerRoles: newDefaultRoles(kind),
        acceptanceMode: DEFAULT_MODE_BY_KIND[kind],
      }],
    });
  };

  /**
   * THE KIND RULE, per row: swapping which document an item points to swaps
   * its kind too, and a kind swap must not leave a stale, now-invalid
   * signerRoles behind — an agreement swapped in from a policy starts with
   * no roles (Save stays disabled until one is picked, never a stale
   * carried-over set the server would refuse); a policy swapped in from an
   * agreement is cleared to none, full stop. The acceptance mode resets to
   * the new kind's default too, since a mode chosen for the old kind may not
   * fit — the operator can still change it afterward.
   */
  const changeItemDocument = (idx: number, documentId: string) => {
    if (!editor) return;
    const doc = documentById.get(documentId);
    if (!doc) return;
    const kind = doc.kind as 'policy' | 'agreement';
    patchItem(idx, {
      documentId,
      kind,
      signerRoles: kind === 'agreement' ? newDefaultRoles(kind) : [],
      acceptanceMode: DEFAULT_MODE_BY_KIND[kind],
    });
  };

  /** Options for one row's document picker: active documents not already used by a DIFFERENT row. */
  const documentOptionsFor = (idx: number) => {
    if (!editor) return [];
    const usedElsewhere = new Set(editor.items.filter((_, i) => i !== idx).map((i) => i.documentId));
    return documents
      .filter((d) => !usedElsewhere.has(d.documentId))
      .map((d) => ({ value: d.documentId, label: d.title, subtitle: `${categoryLabel(d.category)} · ${d.kind}` }));
  };

  const subjectIdValid = (id: string) => SUBJECT_ID_RE.test(id.trim());

  const canSaveEditor = !!editor
    && !!editor.subjectType
    && subjectIdValid(editor.subjectId)
    && editor.items.every((i) => !!i.documentId && (i.kind !== 'agreement' || i.signerRoles.length > 0));

  const saveEditor = async () => {
    if (!editor || !canSaveEditor || !editor.subjectType) return;
    setSaving(true);
    try {
      const body = { items: editor.items, note: editor.note.trim() || undefined };
      await documentsExtApi.saveBinding(editor.subjectType, editor.subjectId.trim(), body);
      toastService.success(`Binding saved for ${subjectTypeLabel(editor.subjectType)} “${editor.subjectId.trim()}”`);
      closeEditor();
      await load();
    } catch (e: any) {
      toastService.error(e?.message || 'binding_save_failed');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await documentsExtApi.deleteBinding(deleteTarget.subjectType, deleteTarget.subjectId);
      toastService.success(`Binding removed for ${subjectTypeLabel(deleteTarget.subjectType)} “${deleteTarget.subjectId}”`);
      setDeleteTarget(null);
      closeEditor();
      await load();
    } catch (e: any) {
      toastService.error(e?.message || 'binding_delete_failed');
    } finally {
      setDeleting(false);
    }
  };

  /* ── preview ──────────────────────────────────────────────────────────── */

  const addPreviewSubject = (s: BindingSubject) => {
    if (!s.subjectType || !subjectIdValid(s.subjectId)) return;
    if (previewSubjects.some((p) => subjectKey(p) === subjectKey(s))) return;
    setPreviewSubjects([...previewSubjects, { subjectType: s.subjectType, subjectId: s.subjectId.trim() }]);
    setPreviewResult(null);
  };

  const addPreviewFromForm = () => {
    if (!previewType) return;
    addPreviewSubject({ subjectType: previewType, subjectId: previewId });
    setPreviewId('');
  };

  const addCurrentEditorToPreview = () => {
    if (!editor || editor.isNew || !editor.subjectType) return;
    addPreviewSubject({ subjectType: editor.subjectType, subjectId: editor.subjectId });
  };

  const removePreviewSubject = (s: BindingSubject) => {
    setPreviewSubjects(previewSubjects.filter((p) => subjectKey(p) !== subjectKey(s)));
    setPreviewResult(null);
  };

  const runPreview = async () => {
    if (previewSubjects.length === 0) return;
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const res = await documentsExtApi.resolveBindings(previewSubjects);
      setPreviewResult(res);
    } catch (e: any) {
      setPreviewError(e?.message || 'binding_resolve_failed');
      toastService.error(e?.message || 'binding_resolve_failed');
    } finally {
      setPreviewLoading(false);
    }
  };

  const anyGuardStale = SUBJECT_TYPES === FALLBACK_SUBJECT_TYPES
    || ACCEPTANCE_MODE_LIST === FALLBACK_ACCEPTANCE_MODES
    || DEFAULT_MODE_BY_KIND === FALLBACK_DEFAULT_MODE_BY_KIND
    || SIGNER_ROLE_LIST === FALLBACK_SIGNER_ROLES;

  return (
    <StaffPage
      section="Legal"
      tabs={<LegalNav />}
      icon={Link2}
      title="Bindings"
      subtitle="Which documents a device, financing term, package, checkout, business type or jurisdiction owes — one row edit, not a deploy."
      actions={(
        <>
          <Button onClick={() => void load()} disabled={loadingList}>
            {loadingList ? <RubyGemMark size={16} drawing color="currentColor" /> : <RefreshCw className="h-4 w-4" />}
            Refresh
          </Button>
          <Button variant="primary" onClick={openCreate}>
            <Plus className="h-4 w-4" />
            New binding
          </Button>
        </>
      )}
    >
      <div className="space-y-4">
        {anyGuardStale && (
          <div className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 px-3.5 py-3 text-[12.5px] leading-relaxed text-red-800">
            <AlertTriangle size={15} className="mt-0.5 shrink-0" />
            <span>
              This page loaded with a stale build of the linked shared-types package —
              some pickers are running on compiled-in fallback lists rather than the live
              exports. Restart the dev server (deleting <code>node_modules/.vite</code> alone
              will not fix it while the server keeps running) and reload this page.
            </span>
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[320px_1fr]">
          {/* ── left: binding rows, grouped by subject type ────────────── */}
          <div className="flex max-h-[70vh] flex-col overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm">
            <div className="border-b border-stone-200 px-4 py-3">
              <h2 className="text-[13px] font-semibold text-stone-900">Registry</h2>
              <p className="text-[11.5px] text-stone-500">Grouped by subject type.</p>
            </div>
            <div className="flex-1 overflow-y-auto">
              {loadingList && (
                <RubyLoader variant="section" label="Loading bindings…" />
              )}
              {listError && !loadingList && (
                <div className="p-3"><LightWarn>Bindings could not be loaded: {listError}.</LightWarn></div>
              )}
              {!loadingList && !listError && groups.length === 0 && (
                <p className="px-4 py-4 text-[12.5px] text-stone-500">
                  No bindings yet. A device with nothing bound owes nothing and blocks nothing —
                  start with “New binding”.
                </p>
              )}
              {!loadingList && !listError && groups.map((g) => (
                <div key={g.subjectType}>
                  <div className="sticky top-0 border-b border-stone-100 bg-stone-50 px-4 py-1.5 text-[10.5px] font-semibold uppercase tracking-wider text-stone-500">
                    {SUBJECT_TYPE_LABEL[g.subjectType]}
                  </div>
                  {g.rows.map((row) => {
                    const key = subjectKey(row);
                    const active = key === selectedKey;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => void selectBinding(row)}
                        className={`flex w-full min-h-[52px] items-center justify-between gap-2 border-b border-stone-100 px-4 py-2.5 text-left transition-colors ${
                          active ? 'bg-red-50' : 'hover:bg-stone-50'
                        }`}
                      >
                        <span className={`truncate text-[12.5px] ${active ? 'font-semibold text-red-800' : 'text-stone-800'}`}>
                          {row.subjectId}
                        </span>
                        <span className="shrink-0 rounded-full bg-stone-100 px-2 py-0.5 text-[10.5px] font-medium text-stone-600">
                          {row.items.length} doc{row.items.length === 1 ? '' : 's'}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          {/* ── right: editor ───────────────────────────────────────────── */}
          <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm md:p-5">
            {!editor && (
              <div className="flex min-h-[200px] flex-col items-center justify-center gap-2 text-center text-stone-400">
                <Link2 className="h-8 w-8" />
                <p className="text-[13px]">Pick a binding on the left, or start a new one.</p>
              </div>
            )}

            {editor && loadingEditor && (
              <RubyLoader variant="section" label="Loading binding…" />
            )}

            {editor && !loadingEditor && (
              <div className="space-y-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h2 className="text-[14px] font-semibold text-stone-900">
                      {editor.isNew ? 'New binding' : `${subjectTypeLabel(editor.subjectType)} · ${editor.subjectId}`}
                    </h2>
                    <p className="text-[11.5px] text-stone-500">
                      {editor.isNew
                        ? 'Subject type and id lock once saved — creating a new one never overwrites another.'
                        : 'The subject is fixed. Delete and recreate to move these documents to a different subject.'}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {!editor.isNew && (
                      <Button
                        onClick={addCurrentEditorToPreview}
                        disabled={previewSubjects.some((p) => subjectKey(p) === subjectKey({
                          subjectType: editor.subjectType, subjectId: editor.subjectId,
                        }))}
                      >
                        <Search className="h-4 w-4" /> Add to preview
                      </Button>
                    )}
                    {!editor.isNew && (
                      <Button
                        variant="danger"
                        onClick={() => setDeleteTarget({
                          subjectType: editor.subjectType as BindingSubjectType, subjectId: editor.subjectId,
                        })}
                      >
                        <Trash2 className="h-4 w-4" /> Delete
                      </Button>
                    )}
                    <Button onClick={closeEditor} disabled={saving}>Cancel</Button>
                    <Button variant="primary" onClick={() => void saveEditor()} disabled={!canSaveEditor || saving}>
                      {saving ? <RubyGemMark size={16} drawing color="currentColor" /> : <Save className="h-4 w-4" />}
                      {saving ? 'Saving…' : 'Save'}
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <FieldLabel requirement="required">Subject type</FieldLabel>
                    <SearchableDropdown
                      options={SUBJECT_TYPE_OPTIONS}
                      value={editor.subjectType || null}
                      onChange={(v) => setEditor({ ...editor, subjectType: (v as BindingSubjectType) || '' })}
                      placeholder="Choose a subject type…"
                      disabled={!editor.isNew}
                    />
                  </div>
                  <div>
                    <FieldLabel requirement="required">Subject id</FieldLabel>
                    <input
                      className={INPUT_CLASS}
                      value={editor.subjectId}
                      onChange={(e) => setEditor({ ...editor, subjectId: e.target.value })}
                      placeholder="K10"
                      disabled={!editor.isNew}
                    />
                    {editor.isNew && editor.subjectId && !subjectIdValid(editor.subjectId) && (
                      <p className="mt-1 text-[11px] text-red-600">
                        Letters, digits, underscore, dot and hyphen only — up to 64 characters.
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <FieldLabel>Note</FieldLabel>
                  <input
                    className={INPUT_CLASS}
                    value={editor.note}
                    onChange={(e) => setEditor({ ...editor, note: e.target.value.slice(0, 500) })}
                    placeholder="Why this subject owes these documents (optional)"
                  />
                  <p className="mt-1 text-[10.5px] text-stone-400">{editor.note.length}/500</p>
                </div>

                <div>
                  <FieldLabel>Add document</FieldLabel>
                  <SearchableDropdown
                    value={null}
                    placeholder="Choose a document to bind…"
                    options={documents
                      .filter((d) => !editor.items.some((i) => i.documentId === d.documentId))
                      .map((d) => ({ value: d.documentId, label: d.title, subtitle: `${categoryLabel(d.category)} · ${d.kind}` }))}
                    onChange={(v) => { if (v) addItem(v); }}
                  />
                </div>

                {editor.items.length === 0 ? (
                  <p className="text-[12.5px] text-stone-500">
                    No documents bound to this subject yet — no binding means no document.
                  </p>
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-stone-200">
                    <table className="w-full text-left text-[12px]">
                      <thead>
                        <tr className="border-b border-stone-200 bg-stone-50 text-[10.5px] uppercase tracking-wider text-stone-500">
                          <th className="px-3 py-2 min-w-[220px]">Document</th>
                          <th className="px-3 py-2">Required</th>
                          <th className="px-3 py-2 min-w-[200px]">Signer roles</th>
                          <th className="px-3 py-2 min-w-[170px]">Acceptance mode</th>
                          <th className="px-3 py-2 w-[80px]">Order</th>
                          <th className="px-3 py-2" />
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-stone-100">
                        {editor.items.map((item, idx) => {
                          const doc = documentById.get(item.documentId);
                          return (
                            <tr key={`${item.documentId}-${idx}`} className="align-top">
                              <td className="px-3 py-2.5">
                                <SearchableDropdown
                                  value={item.documentId}
                                  onChange={(v) => { if (v) changeItemDocument(idx, v); }}
                                  options={documentOptionsFor(idx)}
                                  placeholder="Choose a document…"
                                  compact
                                />
                                {doc && (
                                  <p className="mt-1 truncate text-[10.5px] text-stone-400">
                                    {categoryLabel(doc.category)} · {doc.kind}
                                  </p>
                                )}
                              </td>
                              <td className="px-3 py-2.5">
                                <Toggle
                                  on={item.required}
                                  onClick={() => patchItem(idx, { required: !item.required })}
                                  label={`Required for ${doc?.title ?? item.documentId}`}
                                />
                              </td>
                              <td className="px-3 py-2.5">
                                {item.kind === 'agreement' ? (
                                  <div className="flex flex-wrap gap-1.5">
                                    {OFFERED_ROLES.map((role) => (
                                      <button
                                        key={role}
                                        type="button"
                                        onClick={() => toggleRole(idx, role)}
                                        className={`min-h-[28px] rounded-full border px-2.5 py-1 text-[11px] ${
                                          item.signerRoles.includes(role)
                                            ? 'border-red-300 bg-red-50 text-red-800'
                                            : 'border-stone-300 bg-white text-stone-500 hover:bg-stone-50 hover:text-stone-800'
                                        }`}
                                      >
                                        {ROLE_LABEL[role] ?? role}
                                      </button>
                                    ))}
                                    {item.signerRoles.length === 0 && (
                                      <span className="text-[11px] text-red-600">Pick at least one</span>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-[11px] italic text-stone-400">Accepted, not signed — policy</span>
                                )}
                              </td>
                              <td className="px-3 py-2.5">
                                <SearchableDropdown
                                  value={item.acceptanceMode}
                                  onChange={(v) => { if (v) patchItem(idx, { acceptanceMode: v as AcceptanceMode }); }}
                                  options={ACCEPTANCE_MODE_OPTIONS}
                                  compact
                                />
                              </td>
                              <td className="px-3 py-2.5">
                                <input
                                  type="number"
                                  className="w-16 min-h-[36px] rounded-lg border border-stone-300 bg-white px-2 text-[12px] text-stone-900 focus:border-admin-primary focus:outline-none focus:ring-1 focus:ring-admin-primary"
                                  value={item.sectionOrder ?? ''}
                                  placeholder="auto"
                                  onChange={(e) => {
                                    const raw = e.target.value;
                                    if (raw === '') { patchItem(idx, { sectionOrder: undefined }); return; }
                                    const n = Math.trunc(Number(raw));
                                    if (Number.isFinite(n)) patchItem(idx, { sectionOrder: Math.max(1, n) });
                                  }}
                                />
                              </td>
                              <td className="px-3 py-2.5">
                                <button
                                  type="button"
                                  aria-label={`Remove ${doc?.title ?? item.documentId}`}
                                  onClick={() => removeItem(idx)}
                                  className="flex h-9 w-9 items-center justify-center rounded-lg text-stone-400 hover:bg-red-50 hover:text-red-600"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── preview: what a scenario would actually owe ─────────────────── */}
        <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm md:p-5">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div>
              <h2 className="text-[14px] font-semibold text-stone-900">Preview</h2>
              <p className="text-[11.5px] text-stone-500">
                Pick the subjects a real scenario would name — the resolved list is exactly what the binder will build.
              </p>
            </div>
            <Button variant="primary" onClick={() => void runPreview()} disabled={previewSubjects.length === 0 || previewLoading}>
              {previewLoading ? <RubyGemMark size={16} drawing color="currentColor" /> : <Search className="h-4 w-4" />}
              {previewLoading ? 'Resolving…' : 'Preview'}
            </Button>
          </div>

          <div className="flex flex-wrap items-end gap-2">
            <div className="w-48">
              <FieldLabel>Subject type</FieldLabel>
              <SearchableDropdown
                options={SUBJECT_TYPE_OPTIONS}
                value={previewType || null}
                onChange={(v) => setPreviewType((v as BindingSubjectType) || '')}
                placeholder="Type…"
              />
            </div>
            <div className="w-40">
              <FieldLabel>Subject id</FieldLabel>
              <input
                className={INPUT_CLASS}
                value={previewId}
                onChange={(e) => setPreviewId(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') addPreviewFromForm(); }}
                placeholder="K10"
              />
            </div>
            <button
              type="button"
              onClick={addPreviewFromForm}
              disabled={!previewType || !subjectIdValid(previewId)}
              className="inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-lg border border-stone-300 bg-white px-4 text-[13px] text-stone-700 hover:bg-stone-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Plus className="h-4 w-4" /> Add subject
            </button>
          </div>

          {previewSubjects.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {previewSubjects.map((s) => (
                <span
                  key={subjectKey(s)}
                  className="inline-flex min-h-[32px] items-center gap-1.5 rounded-full border border-stone-300 bg-stone-50 px-3 py-1 text-[12px] text-stone-700"
                >
                  {subjectTypeLabel(s.subjectType)}: {s.subjectId}
                  <button
                    type="button"
                    aria-label={`Remove ${subjectKey(s)} from preview`}
                    onClick={() => removePreviewSubject(s)}
                    className="ml-0.5 flex h-5 w-5 items-center justify-center rounded-full text-stone-400 hover:bg-stone-200 hover:text-stone-700"
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          )}

          {previewError && <div className="mt-3"><LightWarn>Preview failed: {previewError}.</LightWarn></div>}

          {previewResult && (
            <div className="mt-4">
              <p className="mb-2 text-[11.5px] text-stone-500">
                {previewResult.items.length} item{previewResult.items.length === 1 ? '' : 's'} resolved
                from {previewResult.boundSubjectCount} of {previewResult.subjectCount} subject{previewResult.subjectCount === 1 ? '' : 's'} with a binding.
              </p>
              {previewResult.items.length === 0 ? (
                <p className="text-[12.5px] text-stone-500">Nothing owed — no binding matched, or every matched document is retired.</p>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-stone-200">
                  <table className="w-full text-left text-[12px]">
                    <thead>
                      <tr className="border-b border-stone-200 bg-stone-50 text-[10.5px] uppercase tracking-wider text-stone-500">
                        <th className="px-3 py-2">Document</th>
                        <th className="px-3 py-2">Category</th>
                        <th className="px-3 py-2">Required</th>
                        <th className="px-3 py-2">Signer roles</th>
                        <th className="px-3 py-2">Acceptance mode</th>
                        <th className="px-3 py-2 min-w-[200px]">Bound by</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100">
                      {previewResult.items.map((item) => (
                        <tr key={item.documentId}>
                          <td className="px-3 py-2.5">
                            <p className="font-medium text-stone-900">{item.title}</p>
                            <p className="text-[10.5px] text-stone-400">{item.kind}</p>
                          </td>
                          <td className="px-3 py-2.5 text-stone-600">{categoryLabel(item.category)}</td>
                          <td className="px-3 py-2.5">
                            {item.required ? (
                              <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10.5px] font-semibold text-red-800">Required</span>
                            ) : (
                              <span className="rounded-full bg-stone-100 px-2 py-0.5 text-[10.5px] text-stone-600">Optional</span>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-stone-600">
                            {item.signerRoles.length ? item.signerRoles.map((r) => ROLE_LABEL[r] ?? r).join(', ') : '—'}
                          </td>
                          <td className="px-3 py-2.5 text-stone-600">{ACCEPTANCE_MODE_LABEL[item.acceptanceMode] ?? item.acceptanceMode}</td>
                          <td className="px-3 py-2.5">
                            <div className="flex flex-wrap gap-1">
                              {item.boundBy.map((key) => {
                                const [t, ...rest] = key.split('#');
                                const id = rest.join('#');
                                return (
                                  <span key={key} className="rounded-full bg-blue-50 px-2 py-0.5 text-[10.5px] text-blue-800">
                                    {subjectTypeLabel(t)}: {id}
                                  </span>
                                );
                              })}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        open={!!deleteTarget}
        title="Delete this binding?"
        message={deleteTarget && (
          <>
            <strong>{subjectTypeLabel(deleteTarget.subjectType)} “{deleteTarget.subjectId}”</strong> will stop
            owing every document listed here. Existing signed or acknowledged records are untouched — this
            only stops NEW obligations from being created for this subject.
          </>
        )}
        confirmLabel="Delete binding"
        tone="rose"
        busy={deleting}
        onConfirm={() => void confirmDelete()}
        onCancel={() => setDeleteTarget(null)}
      />
    </StaffPage>
  );
}
